import * as React from 'react';

// @ts-expect-error - imported from .js/.jsx file
import {ZoomIcon} from 'shells/icon/svg/zoom-icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {ModalHeader} from 'shells/typography/modal-header';

import type {MeetingUpdateInput} from 'nutshell-graphql-types';

import {authToService} from '../../../setup/integrations/auth-to-service';

import {ConnectButton} from './connect-button';
import {ConnectedWithReconfigureButton} from './connected-with-reconfigure-button';
import {DoneButton} from './done-button';
import {useGetZoomIntegration} from './graphql/hooks/use-get-zoom-integration';
import {useSetZoomIsEnabled} from './graphql/hooks/use-set-zoom-is-enabled';

import styles from './conferencing-modal-wrapper.module.css';

type Props = {
    meetingId: string;
    onSave: (arg1: MeetingUpdateInput) => void;
    closeModal: () => void;
    onFailToConnect?: () => void;
    windowFeatures: any;
};

export function ZoomModalContent(props: Props) {
    const [error, setError] = React.useState<string | null | undefined>(null);

    const {zoomIntegration, refetch} = useGetZoomIntegration();
    const isZoomEnabled = zoomIntegration ? zoomIntegration.isEnabled : false;
    const setZoomIsEnabled = useSetZoomIsEnabled();

    const handleEnableZoom = () => {
        setZoomIsEnabled({isEnabled: true}).then((mutationResults) => {
            const authUrl =
                mutationResults && mutationResults.data && mutationResults.data.setZoomIsEnabled
                    ? mutationResults.data.setZoomIsEnabled.authUrl
                    : null;

            if (authUrl) {
                authToService({
                    authUrl,
                    windowFeatures: props.windowFeatures,
                    authCheckUrl: '/zoom/has-auth',
                })
                    .then(({hasAuth}) => {
                        if (hasAuth) {
                            refetch();
                        }
                    })
                    .catch((errors) => setError(errors[0]));
            }
        });
    };

    const handleReconfigureClick = () => {
        setZoomIsEnabled({isEnabled: false}).then(() => handleEnableZoom());
    };

    const handleSave = () => {
        if (props.onSave) {
            props.onSave({
                id: props.meetingId,
                location: null,
                locationType: isZoomEnabled ? 'ZOOM' : undefined,
            });
        }
    };

    const handleClose = () => {
        if (!isZoomEnabled && props.onFailToConnect) {
            props.onFailToConnect();
        } else {
            props.closeModal();
        }
    };

    return (
        <div className={styles['content-container']}>
            <ModalHeader>Connect your Zoom account</ModalHeader>
            <StackLegacy spacing={16}>
                <div>
                    Log in and authenticate with your Zoom account to automatically generate meeting
                    links whenever you select this option as your meeting location.
                </div>
                <div>
                    Virtual meeting links will be created and sent to all participants for each
                    booked meeting.
                </div>
                {isZoomEnabled ? (
                    <ConnectedWithReconfigureButton
                        integrationName='Zoom'
                        onClick={handleReconfigureClick}
                    />
                ) : (
                    <ConnectButton
                        icon={ZoomIcon}
                        integrationName='Zoom'
                        onClick={handleEnableZoom}
                    />
                )}
                {error ? <div className={styles.error}>{error}</div> : undefined}
            </StackLegacy>
            {/* @ts-expect-error */}
            <DoneButton onSave={props.onSave ? handleSave : undefined} closeModal={handleClose} />
        </div>
    );
}
